import { useTheme } from '@emotion/react';
import Form, { useForm } from 'rc-field-form';
import React from 'react';
import Xregexp from 'xregexp';

import { Address, SearchAddress } from 'api/api';

import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { FormLayout } from '~src/commons/components/layout/form-layout';
import { useLogPosthogEvent } from '~src/commons/services/posthog-service';
import { useAriaTitle } from '~src/commons/utils/hooks/a11y-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';

import { isStreetNumberReversed } from '~address-form/config/template-config';
import { isAddressEmpty } from '~address-form/utils/address-utils';
import { formatAddress } from '~address-form/utils/format';

import { FullscreenComponent } from '../../fullscreen-internals/fullscreen-mode';
import { useGlobalState } from '../../global-state';
import { mediaQueries } from '../../theme/mediaQueries';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { DialogLayout } from '../../ui/dialog-layout';
import { Field } from '../../ui/field';
import { TextInput } from '../../ui/text-input';
import { AddressProvider } from './address-provider';
import { AutocompleteInputFacade } from './autocomplete-input';
import { PostalCodeField } from './postal-code-field';
import { RegionInput } from './region-input';
import { getRegions } from './regions';

interface AddressFormLayoutProps {
  fields: Record<string, React.ReactElement | undefined>;
}

const AddressFormLayout: React.FC<AddressFormLayoutProps> = ({ fields }) => {
  return (
    <div>
      {fields.street && <div style={{ marginBottom: '16px' }}>{fields.street}</div>}
      <div
        css={{
          display: 'flex',
          [mediaQueries.S]: {
            flexDirection: 'column',
          },
        }}
      >
        <div css={{ flexGrow: 1 }}>{fields.postal_code}</div>
        {fields.city && (
          <div
            css={{
              flexGrow: 1,
              margin: '0 0 0 16px',
              [mediaQueries.S]: {
                margin: '16px 0 0 0',
              },
            }}
          >
            {fields.city}
          </div>
        )}
      </div>
      {fields.region && <div style={{ marginTop: '16px' }}>{fields.region}</div>}
    </div>
  );
};

interface AddressFormFullscreenProps {
  onClose: () => void;
  onSubmit: (values: SearchAddress) => void;
}

export const AddressFormFullscreen: FullscreenComponent<AddressFormFullscreenProps> = ({
  onSubmit,
  onClose,
}) => {
  const state = useGlobalState();
  // TODO: move the config to global state.
  const session = state.sessions[0];

  const [formInstance] = useForm();

  const { tICU } = useTranslations();

  const country = state.purchase_country;

  const streetField = (
    <Field
      name={['address_lines', 0]}
      rules={[
        {
          required: session.flags.require_street_on_address_module,
          message: tICU('widget.ADDRESS_MODAL.STREET_REQUIRED_VALIDATION'),
        },
        {
          pattern: country === 'NO' ? RegExp(/^((?!postbox).)*$/i) : undefined,
          message: tICU('widget.ADDRESS_MODAL.STREET_VALIDATION'),
        },
        {
          pattern: isStreetNumberReversed(country)
            ? RegExp(/^[a-z0-9]+.*/i)
            : Xregexp('\\pL+\\s+[0-9].*'),
          message: tICU('widget.ADDRESS_MODAL.STREET_VALIDATION'),
        },
      ]}
    >
      {(props, meta) => (
        <AutocompleteInputFacade
          {...props}
          enableAutocomplete={session.flags.autocomplete_street}
          label={tICU('widget.ADDRESS_MODAL.STREET_PLACEHOLDER')}
          error={meta.errors[0]}
          country={country}
          formInstance={formInstance}
          name="street"
          required={session.flags.require_street_on_address_module}
        />
      )}
    </Field>
  );

  const postalCodeField = (
    <PostalCodeField name="postal_code" required>
      {inputProps => (
        <TextInput {...inputProps} label={tICU('widget.ADDRESS_MODAL.POSTALCODE_PLACEHOLDER')} />
      )}
    </PostalCodeField>
  );

  const cityField = (
    <Field
      name="city"
      rules={[
        {
          required: true,
          message: tICU('widget.ADDRESS_MODAL.CITY_REQUIRED_VALIDATION'),
        },
      ]}
    >
      {(props, meta) => (
        <TextInput
          {...props}
          autoComplete="address-level2"
          label={tICU('widget.ADDRESS_MODAL.CITY_PLACEHOLDER')}
          error={meta.errors[0]}
          required
          name="city"
        />
      )}
    </Field>
  );

  const regionList = getRegions(country);

  const regionField = (
    <Field
      name="region"
      rules={[
        {
          required: session.flags.require_region,
          message: tICU('widget.ADDRESS_MODAL.REGION_REQUIRED_VALIDATION'),
        },
      ]}
    >
      {(props, meta) => (
        <RegionInput
          {...props}
          autoComplete="address-level1"
          label={tICU('widget.ADDRESS_MODAL.REGION_PLACEHOLDER')}
          error={meta.errors[0]}
          required={session.flags.require_region}
          data-name="region"
        />
      )}
    </Field>
  );

  const title = session.flags.show_street_on_address_module
    ? tICU('widget.ADDRESS_MODAL.TITLE')
    : tICU('widget.ADDRESS_MODAL.NO_STREET.TITLE');

  return (
    <Dialog width={520} onClose={() => onClose()}>
      <DialogLayout title={title} onClose={onClose}>
        <Form
          css={{ height: '100%' }}
          initialValues={state.search_address}
          form={formInstance}
          onFinish={onSubmit}
        >
          <FormLayout
            submitButton={
              <Button css={{ marginTop: '30px' }} type="submit">
                {tICU('widget.ADDRESS_MODAL.SAVE_BTN')}
              </Button>
            }
          >
            <AddressFormLayout
              fields={{
                street: session.flags.show_street_on_address_module ? streetField : undefined,
                postal_code: postalCodeField,
                city: session.flags.show_city_on_address_module ? cityField : undefined,
                region: session.flags.show_region && regionList.length ? regionField : undefined,
              }}
            />
          </FormLayout>
        </Form>
      </DialogLayout>
    </Dialog>
  );
};
AddressFormFullscreen.fullscreenName = 'AddressFormFullscreen';

interface FormattedAddressProps {
  address: Address;
}

export const FormattedAddress: React.FC<FormattedAddressProps> = ({ address }) => {
  const globalState = useGlobalState();
  const session = globalState.sessions[0];
  const { flags } = session;

  const formattedAddress = formatAddress(address, {
    country: globalState.purchase_country,
    show_street: flags.show_street_on_address_module,
    show_city: flags.show_city_on_address_module,
    show_postal_code: true,
    show_region: flags.show_region,
  });

  return <span>{formattedAddress}</span>;
};

export const AddressFormProxy: React.FC = () => {
  const logPosthogEvent = useLogPosthogEvent();
  const { tICU } = useTranslations();
  const theme = useTheme();

  const state = useGlobalState();
  const searchAddress = state.search_address;

  useAriaTitle(tICU('widget.aria.CHECKOUT_ADDRESS_FORM.TITLE'), 'checkout');

  const text = isAddressEmpty(searchAddress) ? (
    tICU('widget.CUSTOMER_ADDRESS_PLACEHOLDER')
  ) : (
    <FormattedAddress address={searchAddress!} />
  );

  return (
    <AddressProvider>
      {({ onOpen }) => (
        <OpenModalButton
          onClick={() => {
            onOpen();
            logPosthogEvent('Address Label Clicked');
          }}
          title={{ text, color: theme.neutral35 }}
          data-test="address-input-button"
          id="address-input-button"
        />
      )}
    </AddressProvider>
  );
};
